import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeftRightIcon, ListIcon, Logout01Icon, Settings01Icon, SidebarLeft01Icon, UserIcon } from "@hugeicons-pro/core-bulk-rounded";

import { Link, useLocation, useNavigate } from "react-router";
import { useEffect, useRef, useState } from "react";

import {
  Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { logout } from "@/lib/api/auth";

const SECTION_LABELS: Record<string, string> = {
  "": "Overzicht",
  markten: "Markten",
  benchmarks: "Benchmarks",
  ecom: "E-commerce",
  brands: "Merken",
  platforms: "Platformen",
  campagnes: "Campagnes",
  awareness: "Awareness",
  jobs: "Job ads",
  collaborations: "Collaboraties",
  sources: "Bronnen",
  settings: "Instellingen",
};

export function Topbar({
  onToggleSidebar,
  onOpenMobileNav,
}: {
  onToggleSidebar?: () => void;
  onOpenMobileNav?: () => void;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  async function handleLogout() {
    setOpen(false);
    await logout();
    navigate("/login", { replace: true });
  }

  const section = pathname.split("/").filter(Boolean)[0] ?? "";
  const label = SECTION_LABELS[section] ?? section.charAt(0).toUpperCase() + section.slice(1);

  return (
    <header
      className="sticky top-0 z-30 flex shrink-0 items-center gap-3 border-b border-border/60 bg-background/85 px-4 backdrop-blur-xl"
      style={{ height: "var(--chrome-height)", minHeight: "var(--chrome-height)" }}
    >
      <Button
        variant="ghost"
        size="icon"
        onClick={onOpenMobileNav}
        aria-label="Menu openen"
        className="-ml-1 shrink-0 lg:hidden"
      >
        <HugeiconsIcon icon={ListIcon} size={20} />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggleSidebar}
        aria-label="Sidebar inklappen"
        className="-ml-1 hidden shrink-0 lg:inline-flex"
      >
        <HugeiconsIcon icon={SidebarLeft01Icon} size={20} />
      </Button>

      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage className="font-medium">{label}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="ml-auto flex items-center gap-2">
        <div className="relative" ref={dropdownRef}>
          <Button variant="outline" size="icon" onClick={() => setOpen((p) => !p)} aria-expanded={open} aria-label="Accountmenu">
            <HugeiconsIcon icon={UserIcon} size={16} />
          </Button>

          {open && (
            <div className="absolute right-0 top-full z-50 mt-1.5 w-56 overflow-hidden rounded-xl border border-border/60 bg-card shadow-lg">
              <div className="border-b border-border/40 px-3.5 py-2.5">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Aangemeld als</p>
                <p className="truncate text-sm font-medium">{user?.email}</p>
              </div>
              <div className="py-1">
                <Link
                  to="/settings"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2.5 px-3.5 py-2 text-sm transition-colors hover:bg-muted/60"
                >
                  <HugeiconsIcon icon={Settings01Icon} size={14} />
                  Instellingen
                </Link>
                <a
                  href="/managing/"
                  className="flex items-center gap-2.5 px-3.5 py-2 text-sm transition-colors hover:bg-muted/60"
                >
                  <HugeiconsIcon icon={ArrowLeftRightIcon} size={14} />
                  Wissel dashboard
                </a>
              </div>
              <div className="border-t border-border/40 py-1">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2.5 px-3.5 py-2 text-sm text-destructive transition-colors hover:bg-muted/60"
                >
                  <HugeiconsIcon icon={Logout01Icon} size={14} />
                  Uitloggen
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
